/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */
/**  @jsx jsx */

import { css, jsx } from '@emotion/react'
import { find, sortBy } from 'lodash'
import { Fragment } from 'react'
import { defineMessages, injectIntl, useIntl } from 'react-intl'

import type { UseEuiTheme } from '@elastic/eui'
import {
  EuiFlexGroup,
  EuiFlexItem,
  EuiFormLabel,
  EuiHealth,
  EuiIcon,
  EuiLink,
  EuiSkeletonText,
  EuiSpacer,
  EuiText,
  EuiTitle,
  EuiToolTip,
  useEuiTheme,
} from '@elastic/eui'

import type { ProxyInfo } from '@modules/cloud-api/v1/types'
import { CuiAlert } from '@modules/cui/Alert'
import { withErrorBoundary } from '@modules/cui/Boundary'
import { FormattedMessage } from '@modules/i18n'
import type { AsyncRequestState, RegionProxies, Runner } from '@modules/ui-types'

import { useSliderColorPicker } from '@/lib/sliders/colors'
import { getSliderPrettyName } from '@/lib/sliders/messages'
import { getSliderWeight } from '@/lib/sliders/sorting'

import { hasProxyRole } from '../../../lib/hostRoles'
import schedule from '../../../lib/schedule'
import toPercentage from '../../../lib/toPercentage'
import ResourceComments from '../../ResourceComments'

import type { FC } from 'react'
import type { IntlShape } from 'react-intl'

type Props = {
  intl: IntlShape
  regionId: string
  hostId: string
  runner?: Runner
  proxies?: RegionProxies
  proxiesRequest: AsyncRequestState
  fetchProxies: () => void
  startManagingRoles: () => void
}

const messages = defineMessages({
  allocationCountToolTip: {
    id: 'proxy-host.allocation-count-tool-tip',
    defaultMessage: '{count} {name} {count, plural, one {allocation} other {allocations}}',
  },
})

// CSS-in-JS styles converted from proxy.scss
const allocationVizStyles = css({
  maxWidth: '30rem',
})

const allocationVizSectionStyles = (euiTheme: UseEuiTheme['euiTheme']) =>
  css({
    display: 'inline-block',
    '.euiToolTipAnchor': {
      display: 'block',
      height: euiTheme.size.m,
    },
  })

const getProxyInfo = ({
  proxies,
  hostId,
}: {
  proxies?: RegionProxies
  hostId: string
}): ProxyInfo | null => {
  if (!proxies) {
    return null
  }

  const proxyMatch = find(proxies.proxies, (proxyItem) => proxyItem.proxyInfo.runner_id === hostId)

  if (!proxyMatch) {
    return null
  }

  return proxyMatch.proxyInfo
}

const Proxy: FC<Props> = ({ proxies, hostId, regionId, runner, startManagingRoles }) => (
  <Fragment>
    <EuiTitle>
      <h3>
        <FormattedMessage id='proxy-host.title' defaultMessage='Proxy' />
      </h3>
    </EuiTitle>

    <EuiSpacer size='s' />

    <EuiText color='subdued'>
      <FormattedMessage
        id='proxy-host.description'
        defaultMessage='Routes user requests to instances in Elastic Stack deployments.'
      />
    </EuiText>

    <EuiSpacer size='l' />

    <MissingRoleWarning runner={runner} startManagingRoles={startManagingRoles} />

    <MissingProxyError proxies={proxies} hostId={hostId} />

    <ProxyDetails proxies={proxies} hostId={hostId} regionId={regionId} />
  </Fragment>
)

const ProxyDetails = ({
  proxies,
  hostId,
  regionId,
}: {
  proxies?: RegionProxies
  hostId: string
  regionId: string
}) => {
  if (!proxies) {
    return <EuiSkeletonText />
  }

  const proxyInfo = getProxyInfo({ proxies, hostId })

  if (!proxyInfo) {
    return null
  }

  return (
    <Fragment>
      <ProxyStatus proxyInfo={proxyInfo} />

      <EuiSpacer size='xl' />

      <ProxyConnections proxyInfo={proxyInfo} />

      <EuiSpacer size='xl' />

      <ProxyRequestCharts />

      <EuiSpacer size='l' />

      <Comments regionId={regionId} hostId={hostId} />
    </Fragment>
  )
}

const ProxyStatus = ({ proxyInfo }: { proxyInfo: ProxyInfo }) => {
  const { healthy } = proxyInfo

  return (
    <EuiFlexGroup gutterSize='l' alignItems='flexStart'>
      <EuiFlexItem grow={false}>
        <EuiFormLabel>
          <FormattedMessage id='proxy-host.health-label' defaultMessage='Proxy status' />
        </EuiFormLabel>

        <EuiSpacer size='s' />

        <EuiHealth color={healthy ? 'success' : 'danger'}>
          {healthy ? (
            <FormattedMessage id='proxy-host.health-healthy-label' defaultMessage='Healthy' />
          ) : (
            <FormattedMessage id='proxy-host.health-unhealthy-label' defaultMessage='Unhealthy' />
          )}
        </EuiHealth>
      </EuiFlexItem>

      {/* TODO: proxy build hash and version */}
    </EuiFlexGroup>
  )
}

const ProxyConnections = ({ proxyInfo }: { proxyInfo: ProxyInfo }) => {
  const { euiTheme } = useEuiTheme()

  const getSliderColor = useSliderColorPicker()
  const { formatMessage } = useIntl()
  const { allocations } = proxyInfo
  const totalAllocations = allocations.reduce(
    (all, allocation) => all + allocation.counts.allocations,
    0,
  )

  const allocationCounts = sortBy(
    allocations.map(({ allocations_type: sliderInstanceType, counts }) => ({
      sliderInstanceType,
      color: getSliderColor(sliderInstanceType),
      name: <FormattedMessage {...getSliderPrettyName({ sliderInstanceType })} />,
      count: counts.allocations,
    })),
    (allocationCount) => getSliderWeight(allocationCount.sliderInstanceType),
  )

  return (
    <Fragment>
      <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiTitle size='s'>
            <h5>
              <FormattedMessage
                id='proxy-host.allocated-proxy-connections-label'
                defaultMessage='Allocated instance connections'
              />
            </h5>
          </EuiTitle>
        </EuiFlexItem>

        <EuiFlexItem grow={false}>
          <EuiText size='s' color='subdued'>
            {totalAllocations}
          </EuiText>
        </EuiFlexItem>
      </EuiFlexGroup>

      <EuiSpacer size='m' />

      <div css={allocationVizStyles}>
        {allocationCounts.map(({ sliderInstanceType, color, name, count }) => (
          <span
            key={sliderInstanceType}
            css={allocationVizSectionStyles(euiTheme)}
            style={{
              width: `${toPercentage(count / totalAllocations)}%`,
              backgroundColor: color,
            }}
          >
            <EuiToolTip
              content={formatMessage(messages.allocationCountToolTip, { name, count })}
              position='bottom'
            >
              <span />
            </EuiToolTip>
          </span>
        ))}
      </div>

      <EuiSpacer size='l' />

      {allocationCounts.map(({ sliderInstanceType, color, name, count }, index) => (
        <Fragment key={sliderInstanceType}>
          {index > 0 && <EuiSpacer size='s' />}

          <EuiFlexGroup gutterSize='m' alignItems='center' responsive={false}>
            <EuiFlexItem grow={false}>
              <EuiIcon type='dot' color={color} />
            </EuiFlexItem>

            <EuiFlexItem grow={false} style={{ width: '200px', maxWidth: '100%' }}>
              {name}
            </EuiFlexItem>

            <EuiFlexItem grow={false}>{count}</EuiFlexItem>
          </EuiFlexGroup>
        </Fragment>
      ))}
    </Fragment>
  )
}

const ProxyRequestCharts = () => <Fragment>{/* TODO: proxy request charts */}</Fragment>

const MissingProxyError = ({ proxies, hostId }: { hostId: string; proxies?: RegionProxies }) => {
  const proxyInfo = getProxyInfo({ proxies, hostId })

  if (!proxies || proxyInfo) {
    return null
  }

  return (
    <Fragment>
      <CuiAlert type='error' iconType='alert'>
        <FormattedMessage
          id='proxy-host.proxy-not-found'
          defaultMessage="We couldn't find a proxy associated with this host."
        />
      </CuiAlert>

      <EuiSpacer size='m' />
    </Fragment>
  )
}

const MissingRoleWarning = ({
  runner,
  startManagingRoles,
}: {
  runner?: Runner
  startManagingRoles: () => void
}) => {
  if (hasProxyRole({ runner })) {
    return null
  }

  return (
    <Fragment>
      <CuiAlert type='warning' iconType='cloudStormy'>
        <FormattedMessage
          id='proxy-host.host-not-a-proxy'
          defaultMessage="This host doesn't have the Proxy role. You can add it in {manageRoles}."
          values={{
            manageRoles: (
              <EuiLink onClick={startManagingRoles}>
                <FormattedMessage id='proxy-host.manage-roles' defaultMessage='Manage roles' />
              </EuiLink>
            ),
          }}
        />
      </CuiAlert>

      <EuiSpacer size='m' />
    </Fragment>
  )
}

const Comments = ({ regionId, hostId }: { regionId: string; hostId: string }) => (
  <Fragment>
    <EuiTitle size='s'>
      <h5>
        <FormattedMessage id='proxy-host.comments-label' defaultMessage='Comments' />
      </h5>
    </EuiTitle>

    <EuiSpacer size='s' />

    <EuiText size='s' color='subdued'>
      <FormattedMessage
        id='proxy-host.comments-description'
        defaultMessage='Leave notes for yourself for later or for other system administrators.'
      />
    </EuiText>

    <EuiSpacer size='m' />

    <ResourceComments
      spacerBefore={true}
      resourceType='proxy'
      regionId={regionId}
      resourceId={hostId}
    />
  </Fragment>
)

export default withErrorBoundary(
  schedule(injectIntl(Proxy), ({ fetchProxies }) => fetchProxies(), [[`regionId`]]),
)
